import { clone, res, state, ui } from './stores.js';
import { get } from 'svelte/store';
import { allCards, cardByID, getCard, getNeutralZone, getPlayerIndex } from './game.js';
import { endGame } from './turns.js';
import { getHints, ongoingEffects, ongoingPlayer, resolveCount, resolveCountTypes, resolveLocCondition, resolveObject, resolveSubCondition, testNum } from './parse.js';
import { cardsInPlay, matches, myAway, possArray, theirAway, trackTypes } from './card_actions.js';
import { botMarkedScore } from './khan.js';
import { treacheryValues } from './modules.js';
import { countableTraits } from './newcards/types.js';
import { collectiveValues } from './borg.js';
import { countPlayerTokens } from './bot.js';
import { perfMeasure, perfStart } from './perf.js';
 
export async function scoreGame(gameEnd = true){
    let s = get(state);
    if(s.gameEndTrigger == 'Burn'){
        s = await scoreBurn(s);
    }else if(s.gameEndTrigger == 'Domination'){
        s = scoreDomination(s);
    }else{
        s = await scorePlayers(s, s.players, gameEnd);
    }

    state.update(st => {
        return s;
    })
}

async function scorePlayers(s, players, gameEnd = false){
    let max = -Infinity;
    let winners = [];
    for(let p of players){
        p.score = await scorePlayer(p, gameEnd);
        if(p.score > max){
            winners = [ p.captain ];
            max = p.score;
        }else if(p.score == max){
            winners = [...winners, p.captain];
        }
    }
    if(winners.length != s.players.length){ // because if all players score the same, it's a draw
        s.winners = winners;
    }

    return s;
}

async function scoreBurn(s){
    let min = Infinity;
    let winners = [];
    for(let p of s.players){
        if(p.bot && s.players.some(p => !p.bot)){
            winners = [ p ]; // Bot wins all burns in bot v human
            break;
        }
        let incidents = allScoreableCards(p, true).filter(c => c.suits.includes('incident')).length;
        p.score = `${incidents} [[incident]]`;
        if(incidents < min){
            winners = [ p ];
            min = incidents;
        }else if(incidents == min){
            winners = [...winners, p];
        }
    }

    if(winners.length > 1){
        return await scorePlayers(s, winners, true); // Judge between winners through normal scoring
    }else{
        s.winners = winners.map(p => p.captain);
    }

    return s;
}

function scoreDomination(s){
    for(let p of s.players){
        for(let r of get(res).trackTypes){
            if(get(res).trackVP[p[r]] == Infinity){
                s.winners = [ p.captain ];
                p.score = 'Dominator'
            }else{
                p.score = 'Loser'
            }
        }
    }
    return s;
}

function scoreDashMissions(p){
    // only dashboard missions!
   // if(p.bot) return 0; // !! check it is!
    let missionScore = 0;
    for(let c of [...p.dashboard.missions]){
        if(p.bot) c.vp = 0; // Actually log it as 0
        if(c.complete && c.vp) missionScore += c.vp;
    }
    return missionScore;
}

export async function scorePlayer(p, gameEnd = false){
    const end = perfStart('score.scorePlayer', {captain: p && p.captain, gameEnd});

    let origP = p;
    if(!gameEnd) p = clone(p); // so you can modify without issue

    if(p.captain == 'Q' && p.decks.void){
        // will destroy all non-neg cards in void
        let toRemove = p.decks.void.filter(c =>  !c.vp || c.vp > 0);
        p.decks.void = p.decks.void.filter(c => !toRemove.some(cd => cd.uniqueID == c.uniqueID));

        // should then carry through into the rest of the below because of the cloning
    }

    let cardsToScore = allScoreableCards(p, true).filter(c => !c.dashMission);

    p.scoreBreakdown = {
        individualCards: {},
        locationScoring: 0
    };

    p.scoreMultipliers = {};

    let score = 0;

    for(let c of getNeutralZone()){
        /*if(canControlLocation(p, c) && get(prefs).claimLocationsAtEnd){
            await claimLocation(p, c);
        }else{*/
            let tokens = countPlayerTokens(c, p);
            if(!tokens) continue;
            if(!(ongoingPlayer(p).includes('no_nz_scoring') || p.bot && p.captain == 'Borg Queen')){
                //await doAction(p, 'gain_tokens', {token: 'glory', amount: tokens })
                p.scoreBreakdown.locationScoring += tokens;
            }
        /*}*/
    }

    score += p.scoreBreakdown.locationScoring;

    // "You only score for cards with negative [[?vp]], cards with [[borg]], and cards in your
    // Assimilated pile." Each item is an NP filter; union them, then skip anything outside it.
    let onlyAllowed = null;
    if(ongoingPlayer(p).includes('only_score')){
        onlyAllowed = new Set();
        for(let item of ongoingPlayer(p, 'only_score')){
            let elig = cardsToScore.slice();
            let handled = false;
            if(item.qualifier && item.qualifier.type == 'subcondition'){
                elig = resolveSubCondition(p, item.qualifier.condition, elig, {}, null);
                handled = true;
            }
            if(item.types){ // '[[borg]] cards'
                elig = elig.filter(c => matches(c, item.types, false));
                handled = true;
            }
            if(item.location && item.location.place){
                elig = elig.filter(c => (p.decks[item.location.place] || []).includes(c));
                handled = true;
            }
            if(!handled){
                console.warn('only_score: unhandled filter item', item);
                elig = [];
            }
            for(let c of elig) onlyAllowed.add(c);
        }
    }
    if(!p.bot && p.captain == 'Borg Queen'){
        // Her rule, whatever the dashboard wording parsed to (or if it didn't load): negative VP,
        // [[borg]] cards wherever they are, and anything assimilated
        if(!onlyAllowed) onlyAllowed = new Set();
        let assimilated = p.decks.assimilated || [];
        for(let c of cardsToScore.filter(c => c.vp < 0 || matches(c, 'borg', false) || assimilated.includes(c))) onlyAllowed.add(c);
    }

    // For the score screen, which shows these cards at 0 (keyed by uniqueID: copies of a card share an id)
    p.scoreBreakdown.notScored = onlyAllowed ? cardsToScore.filter(c => !onlyAllowed.has(c)).map(c => c.uniqueID) : [];
    p.scoreBreakdown.focus = {};

    p.scoreBreakdown.endGame = await scoreEndgame(p, onlyAllowed); // first in case of gain effects
    score += p.scoreBreakdown.endGame;

    if(!p.locationScoring) p.locationScoring = 0;

    /*let missionScore = 
    for(let c of p.dashboard.missions){
        if(c.complete) missionScore += c.vp;
    }*/
    p.scoreBreakdown.dash_missions = scoreDashMissions(p);
    score += p.scoreBreakdown.dash_missions;

    // Something somewhere is stringifying this, so stop it
    for(let t of ['glory', 'dilithium', 'latinum']){
        if(typeof p[t] == 'string') p[t] = parseInt(p[t]);
    }
    
    let gloryCount = p.glory || 0;

    /*if(p.endGameGain && p.endGameGain.glory){
        gloryCount += p.endGameGain.glory;
    }*/

    score += gloryCount;

    p.scoreBreakdown.glory = gloryCount;

    if(p.bot){
        if(p.captain == 'Burnham'){
            p.scoreBreakdown.resources = (p.dilithium || 0) + Math.floor((p.latinum || 0)/ 2);
            
        }else{
            let resources = (p.dilithium || 0) + (p.latinum || 0);
            resources = Math.floor(resources / 2);
            p.scoreBreakdown.resources = resources;
        }
        score += p.scoreBreakdown.resources;

        if(p.captain == 'Khan'){
            p.scoreBreakdown.marked = botMarkedScore(p);
            score += p.scoreBreakdown.marked;
        }

    }

    let _res = get(res);
    /*for(let t of _res.trackTypes){
        score += _res.trackVP[p[t]];
    }*/

    let dash = p.dashboard;
    if(!dash){
        console.log('Error: no player dashboard for '+p.captain);
    }else{
        p.scoreBreakdown.tracks = 0;
        let highestScore = {score: 0, track: 'research'}
        // The collective multiplier reaches focus cards in the Assimilated pile, and [[borg]] cards
        // wherever they are. The bot ignores this passive, so all its focus cards score
        let assimilatedOnly = ongoingPlayer(p).includes('collective_assimilated_multiplier');
        const onlyAssimilated = (cards) => cards.filter(c => (!assimilatedOnly || get(state).cardLog[c.uniqueID]?.deck == 'assimilated' || matches(c, 'borg', false)) && (!onlyAllowed || onlyAllowed.has(c)));
        for(let t of trackTypes(p)){
            let relevantCards = cardsToScore.filter(c => c.stripe == t);
            relevantCards = onlyAssimilated(relevantCards);
            let threshold = getTrackMultiplier(p, t);
            
            /*let threshold = 0;
            
            for(let i = p[t]; i >= 0; i--){
                if(dash[t][i]){
                    threshold = dash[t][i];
                    break;
                }
            }

            if(p.captain == 'Khan' && p.bot && p.currentChart == 'regular'){
                threshold = 3;
            }*/

            if(threshold){
                dash.trackMultiplier[t] = threshold;
                if(threshold > highestScore.score){
                    highestScore.score = threshold;
                    highestScore.track = t;
                }
                
                // Below handled in function now
                /*if(get(state).gameType == 'basic' && !dash.missions[0].complete || get(state).gameType == 'advanced' && !dash.missions.some(c => c.complete)){
                    // Mission not completed, so tracks score nowt
                    dash.trackScoring[t] = 0;
                    //dash.trackMultiplier[t] = 0;
                    continue;
                }else if(get(state).gameType == 'advanced' && !dash.missions.some(c => c.complete)){
                    // No mission completed, so tracks score nowt
                    dash.trackScoring[t] = 0;
                    //dash.trackMultiplier[t] = 0;
                    continue;
                }*/

                score += relevantCards.length * threshold;
                p.scoreBreakdown.tracks += relevantCards.length * threshold;
                dash.trackScoring[t] = relevantCards.length * threshold;
                for(let c of relevantCards){
                    c.trackScore = dash.trackMultiplier[t];
                    p.scoreBreakdown.focus[c.uniqueID] = c.trackScore;
                    if(c.vp){
                        c.score = (c.endGameScore || 0) + c.vp + dash.trackMultiplier[t];
                    }else{
                        c.score = (c.endGameScore || 0) + dash.trackMultiplier[t];
                    }
                }
                //console.log(dash.trackScoring[t], t, p.captain)
            }else{
                dash.trackScoring[t] = 0;
                dash.trackMultiplier[t] = 0;
            }
        }
        // Score wild-focus cards dynamically
        if(highestScore.score > 0){
            let wildSet = onlyAssimilated(cardsToScore.filter(c => c.stripe == 'wild'));
            let wild = wildSet.length;
            score += wild * highestScore.score;
            p.scoreBreakdown.tracks += wild * highestScore.score;
            dash.trackScoring[highestScore.track] += wild * highestScore.score;
            for(let c of wildSet){
                c.trackScore = highestScore.score;
                p.scoreBreakdown.focus[c.uniqueID] = c.trackScore;
                if(c.vp){
                    c.score = (c.endGameScore || 0) + c.vp + highestScore.score;
                }else{
                    c.score = (c.endGameScore || 0) + highestScore.score;
                }
            }
        }
    }

    let cardScore = 0;

    let no_log_score = ongoingPlayer(p).includes('no_log_score');

    for(let c of cardsToScore){
        if(onlyAllowed && !onlyAllowed.has(c)) continue;

        //if(c.dashMission) continue;
        
        if(ongoingEffects(c).includes('scores_0_if_logged_or_krulmuth')){
            // Check if logged or on krulmuth-B
            let shouldScoreZero = false;
            let info = get(state).cardLog[c.uniqueID];
            if(info.area == 'beamed'){
                let card = getCard(parseInt(info.deck));
                if(matches(card, 'Krulmuth-B')) shouldScoreZero = true;
            }else if(info.deck == 'log'){
                shouldScoreZero = true;
            }
            if(shouldScoreZero) continue; // Don't score this card
        }

        if(c.suits.includes('incident') && ongoingPlayer(p).includes('worse_incidents')){
            let info = ongoingPlayer(p, 'worse_incidents');
            for(let i of info){
                if(typeof i == 'number') score -= i;
            }
        }
        if(ongoingEffects(c).includes('scoring_subtract')){
            let subtract = ongoingEffects(c, 'scoring_subtract');
            if(!subtract.per.obj){
                console.warn('Error: wrong format for scoring subtract');
            }else{
                let count = resolveCountTypes(p, c, subtract.per.obj, 'score_per', {}, false, {wildcardsLeft: 0}); // wildcards are inactive during scoring
                let amount = count * subtract.num;
                if(subtract.per.max) amount = Math.min(amount, subtract.per.max);
                score -= amount;
            }
        }
        let replaced = false;
        if(ongoingEffects(c).includes('scoring_special')){
            //let count = resolveCountTypes(p, c, c.per.obj, 'score_per', {})
            // !! TODO do this by folding out relevant bits
            // NB actually altering the card's VP here – might not be a good idea, but let's see
            let e = ongoingEffects(c, 'scoring_special');
            let sum = 0;
            let replaced = false;
            for(let eff of e){
                sum += await calculateScoreEffect(eff, p, c);
                if(e.replace) replaced = true;
            }
            c.score = sum;
            c.endGameScore = sum;
            score += c.score;
        }
        if(no_log_score){
            let info = get(state).cardLog[c.uniqueID];
            if(info.deck == 'log' && c.vp && c.vp > 0) continue; // skip it because it's in log
        }
        if(matches(c, 'mission') && !c.complete) continue; // Only score completed missions
        if(c.vp && !replaced){
            c.score = (c.endGameScore || 0) + (c.trackScore || 0) + (c.vp || 0);
            score += c.vp; // add VP straight up here, but nothing else
            //c.score = c.vp;
        }

        if(c.score){
            for(let k of Object.keys(p.scoreMultipliers)){
                if(matches(c, k, false)){ // wildcards are inactive during scoring
                    let toAdd = (c.score * (p.scoreMultipliers[k] - 1));
                    c.score += toAdd;
                    score += toAdd;
                }
            }
            p.scoreBreakdown.individualCards[c.id] = c.score;
            //score += c.score; // removed now that endgame score is put into card score
            // so that it doesn't double-count
        }
    }
    p.scoreBreakdown.cards = score;

    //score += scoreMissions(p);
    //p.scoreBreakdown.missions = scoreMissions(p);

    //score += scoreCaptain(p);
   // p.scoreBreakdown.captain = scoreCaptain(p);

    //console.log('score for '+p.captain+': '+score);

    
    
    ui.update(ui => {
        for(let c of cardsToScore){
            if(c.hasOwnProperty('score')){
                ui.cardScores[c.id] = c.score;
            }
        }
        return ui;
    })

    if(!gameEnd) origP.scoreBreakdown = p.scoreBreakdown; // the only thing to map back

    end({captain: origP && origP.captain, gameEnd, score});
    return score;
}

export function getTrackMultiplier(p, t){
    let threshold = 0;
    let dash = p.dashboard;
    
    let list = dash[t];
    if(t == 'treachery'){
        list = treacheryValues();
    }

    if(p.captain == 'Khan' && p.bot && p.currentChart == 'regular'){
        threshold = 3;
    }

    if(p.captain == 'Q' && p.bot){
        // flat threshold
        threshold = 2;
    }

    if(t == 'collective'){
        list = collectiveValues();      
    }else{
        // No need to check missions for Borg Queen as her mission doesn't affect scoring
        // NB: possibly add dashboard passives to validEffect for bot to avoid manualling these
        if((ongoingPlayer(p).includes('collective_assimilated_multiplier') || p.bot && p.captain == 'Borg Queen') && t != 'treachery'){
            // collective is the multiplier for all
            return getTrackMultiplier(p, 'collective');
        }

        if(!p.bot && p.dashSide == 'basic' && !dash.missions[0].complete){
            // Mission not completed, so tracks score nowt
            return 0;
        }
    
        if(!p.bot && p.dashSide == 'advanced' && !dash.missions.some(c => c.complete)){
            // No mission completed, so tracks score nowt
            return 0;
        }
    
        /*if(!p.bot && p.captain == 'Janeway' && p.journeyModule){
            let completedCount = p.decks.log.filter(m => m.suits.includes('mission') && m.traits.includes('journey') && m.complete).length; //p.decks.completed_missions.filter(m => m.traits.includes('journey')).length;
            if(completedCount < 3) return 0; // Minimum of 3 completed
        }*/
        
    }

    

    
    for(let i = p[t]; i >= 0; i--){
        if(list[i]){
            threshold = list[i];
            break;
        }
    }
    return threshold;
}

function testScoreIf(condition, p, c){
    let should = false;
    if(condition == 'no_beamed'){
        if(!c.beamed || c.beamed.length == 0) should = true;
    }else if(condition.type == 'captain_test'){
        for(let c of p.decks.captain){
            if(matches(c, condition.test, false, !condition.neg)){ // wildcards are inactive during scoring
                should = true;
                break;
            }
        }
    }else if(condition.type == 'card_count'){
        let eligible = resolveLocCondition(c, condition.loc, p, allScoreableCards(p, true));
        should = testNum(condition.num, eligible.length);
        console.log('card count', eligible, condition.num, should);
    }else if(condition.type == 'type_count'){
        let count = resolveCountTypes(p, c, condition.obj, 'scoring', {...condition});
        should = testNum(condition.num, count);
    }
    if(condition.neg) return !should;
    return should;
}

async function calculateScoreEffect(e, p, c){
    if(e.scoreIf && !testScoreIf(e.scoreIf, p, c)) return 0;
    if(e.type == 'simple_vp'){
        return e.num;
    }if(e.type == 'scoring_count'){
        let elements = [e];
        if(e.format == 'multiple'){
            elements = e.elements.map(el => { return {num: e.num, ...el}});
        }
        let total = 0;
        for(let el of elements){
            if(el.format == 'track_multiplier'){
                let type = el.track.specific;
                //console.log(p.dashboard.trackScoring[type]);
                total += p.dashboard.trackMultiplier[type] || 0; //p.dashboard.trackScoring[type];
            }else if(el.format == 'track'){
                // Raw step counts
                let track = get(res).trackTypes.find(tr => tr.startsWith(el.track));
                if(!track){
                    console.log('No such track!', el.track)
                    continue; // Non-existent track...!
                }
                total += Math.floor(p[track] / el.div);
            }else if(el.format == 'lowest_track'){
                let lowestScore = Infinity;
                let types = el.tracks;
                //console.log(types, e)
                if(!Array.isArray(types)) types = [types];
                for(let t of types){
                    let track = get(res).trackTypes.find(tr => tr.startsWith(t));
                    if(!track) continue;
                    let score = p[track];
                    if(score < lowestScore) lowestScore = score;
                }
                if(el.div) lowestScore = Math.floor(lowestScore / el.div);
                total += lowestScore;
            }else if(el.format == 'highest_multiplier'){
                let highestScore = 0;
                for(let t of get(res).trackTypes){
                    let score = p.dashboard.trackMultiplier[t];
                    if(score > highestScore) highestScore = score;
                }
                total += highestScore;
            }else if(el.format == 'type_count'){
                // Work through this
                let multiplier = el.num || 1; // parseInt?
                //let divisor = e.obj.num || e.div || 1;
                let count = resolveCountTypes(p, c, el.obj, 'scoring', {...el});
                //if(e.div)
                //console.log(cards, e.num, e.obj.num)
                total += Math.floor(count * multiplier /*/ divisor*/);
            }else if(el.format == 'completed_missions'){
                //let eligible = [...p.decks.missions, ...p.dashboard.missions].filter(c => c.complete);
                //console.log('completed', [...p.decks.missions, ...p.dashboard.missions], eligible)
                let mult = el.num || 1;
                let eligible = [...p.decks.log.filter(c => c.suits.includes('mission')), ...p.dashboard.missions].filter(c => c.complete);
                if(el.filter) eligible = eligible.filter(c => matches(c, el.filter, false)); // wildcards are inactive during scoring
                total += mult * eligible.length;
            }else if(el.format == 'unique_traits'){
                let cards = resolveLocCondition(c, el.location, p, []);
                
                /* if you want wildcards to be wild:
                let allTraits = cards.flatMap(c => c.traits);
                let wc = allTraits.filter(t => t == 'wildcard');
                let nowc = Array.from(new Set(...allTraits.filter(t => t != 'wildcard)));
                count += wc.length + nowc.length; */
                let collatedTraits = cards.filter(c => c.traits).flatMap(c => countableTraits(c.traits));
                let traits = Array.from(new Set(collatedTraits));
                let count = traits.length;
                if(el.div) count = Math.floor(count / el.div);
                if(el.num) count = count * el.num;
                total += count;
            }else if(el.format){
                let multiplier = el.num || 1; // parseInt?
               
                let count = await resolveCount(p, c, el);
                total += Math.floor(count * multiplier /*/ divisor*/);
            }
        }
        if(e.max && total > e.max) total = e.max;
        if(e.hasOwnProperty('min') && total < e.min) total = e.min;
        return total;
    }else if(e.type == 'scoring_test_away'){
        let total = myAway(p, c) + theirAway(p, c);
        if(testNum(e.awayNum, total)){
            return e.num;
        }
    }else if(e.type == 'scoring_test_logged'){
        let info = get(state).cardLog[c.uniqueID];
        let should = false;
        if(info.deck == 'log') should = true;
        if(e.neg) should = !should;
        if(should) return e.num;
    }else if(e.type == 'scoring_location_collective'){
        /* "[[?vp]] per controlled [[location]] you have in play. [[?vp]] is the multiplier of your [[collective]] track" {
        }*/
        let mult = cardsInPlay(p).filter(c => matches(c, 'controlled location')).length;
        let vp = p.dashboard.trackMultiplier.collective;
        return vp * mult;
    }else if(e.type == 'scoring_completed_missions'){
        let sum = 0;
        let extra = [...p.dashboard.missions, ...p.decks.log.filter(c => c.suits.includes('mission'))].filter(c => c.complete); //cardsInPlay(p).filter(c => c.suits.includes('mission') && c.complete);
        for(let c of extra){
            sum += c.vp || 0;
        }
        return sum;
    }
    return 0;
}


async function scoreEndgame(p, onlyAllowed = null){
    let egScore = 0;
    let possExclusives = [];
    p.scoreBreakdown.endGameSpecific = {};
    let cards; 
    let atEndFuncs = [];
    //console.log(cards, cards.flatMap(c => getHints(c, 'endgame')));
    if(p.bot){
        cards = allScoreableCards(p, true);
    }else{
        cards = [...cardsInPlay(p), ...p.dashboard.missions.filter(c => c.complete), ...p.dashboard.passives]; // Is that right?
        // Captain, dashboard and missions aren't 'cards you score for', so 'You only score for' leaves them be
        if(onlyAllowed) cards = cards.filter(c => onlyAllowed.has(c) || c.dashMission || c.dashboardPassive || get(state).cardLog[c.uniqueID]?.deck == 'captain');
    }
    // Beamed cards' endgame ops don't apply - only cards actually in play/logged etc.
    cards = cards.filter(c => get(state).cardLog[c.uniqueID]?.area != 'beamed');
    cards = cards.filter(c => getHints(c, 'endgame').length > 0);
    for(let c of cards){
        let cardScore = 0;
        if(p.bot){
            cardScore = 5; // Standard extra value for EG cards
        }else try{
            let hints = getHints(c, 'endgame');
            if(!hints) continue;
            hints = hints.flat(3);
            for(let h of hints){
                let eff = h.effects;
                if(!eff) continue;
                eff = eff.flat(3);
                let cachedLast = {
                    e: false,
                    score: 0
                };
                for(let e of eff){
                    if(e.type == 'action'){
                        if(e.verb == 'gain' && e.from && e.from.card == 'this card' && e.obj && e.obj.num == 'all'){
                            // transfer from a card
                            /*if(!p.backupResources) p.backupResources = {};
                            if(!p.backupResources[e.obj.types]){
                                p.backupResources[e.obj.types] = p[e.obj.types];
                            }*/
                            if(!p[e.obj.types]) p[e.obj.types] = 0;
                            p[e.obj.types] += parseInt(c[e.obj.types]) || 0;
                            c[e.obj.types] = 0; // So that it can't trigger repeated times
                        }else{
                            if(!p.endGameAlert)alert('Warning: endgame op includes an unsupported action. Report as a bug');
                            p.endGameAlert = true;
                        }
                    }else if(e.type == 'score_icons_exclusive'){
                        /*if(e.icons){
                            cachedExclusiveIcons = e.icons;
                        }else{
                            if(cachedExclusiveIcons){
                                e.icons = cachedExclusiveIcons;
                            }else{
                                throw 'No previous icons to score!'
                            }
                        }*/
                        if(!cachedLast.e) throw 'No previous score to replace!'
                        if(e.traitThreshold){
                            let count = p.collected_traits ? p.collected_traits.length : 0;
                            if(count < e.traitThreshold) continue; // Skip it
                        }
                        let score = await calculateScoreEffect(cachedLast.e, p, c);
                        cardScore -= cachedLast.score; // remove previous
                        cardScore += score; // add this
                        cachedLast.score = score; // In case of chained
                       // let matching = cardsAndMissions(p).filter(c => matches(c, e.icons)).map(c => c.id);
                        //let score = cardsAndMissions(p).reduce((a,c) => a + matches(c, e.icons) || 0, 0) * e.num;
                        /*possExclusives.push({
                            id: c.id,
                            score: score,
                            per: e.num,
                            matching
                        });*/
                        //cardScore = 0;
                    }else if(e.type == 'double_score'){
                        /*atEndFuncs.push((p) => {
                            let cards = allScoreableCards(p, true).filter(c => matches(c, e.doubleType));
                            let toAdd = 0;
                            for(let c of cards){
                                toAdd += c.score;
                                c.score *= 2;
                            }
                            
                            return p;
                        })*/
                       for(let t of possArray(e.doubleType)){
                        if(!p.scoreMultipliers[t]) p.scoreMultipliers[t] = 1;
                        p.scoreMultipliers[t] = p.scoreMultipliers[t] * 2;
                       }
                       
                    }else{
                        //if(e.icons) cachedExclusiveIcons = e.icons;
                        let score = await calculateScoreEffect(e, p, c);
                        cachedLast = {
                            e,
                            score
                        }
                        cardScore += score;
                        // Should handle additional automatically I think??
                    }
                }
            }
        }catch(err){
            // One card's endgame op failing used to abort the whole score, leaving the last
            // score showing and the breakdown half-built
            console.error(`Endgame scoring failed for ${c.name}`, err);
            cardScore = 0;
        }

        egScore += cardScore;
        //console.log(egScore, cardScore)
        p.scoreBreakdown.endGameSpecific[c.id.replace('/', '_')] = cardScore; // was [c.id]
        c.endGameScore = cardScore;
        if(c.vp && !isNaN(parseInt(c.vp))){
            c.score = parseInt(c.vp) + cardScore;
        }else{
            c.score = cardScore;
        }

        //console.log(`score for ${c.name}: ${c.vp}+${cardScore}=${c.score}`)
    }

    /*if(possExclusives.length > 0){
        let ex = possExclusives.sort((a,b) => b.score - a.score)[0];
        p.scoreBreakdown.endGameSpecific[ex.id.replace('/', '_')] = ex.score; //  was [ex.id]
        egScore += ex.score;
        for(let id of ex.matching){
            let c = cardsAndMissions(p).find(cd => cd.id == id);
            if(c){
                c.score = ex.per;
            }
        }
        // Add to card if needed!
    }*/

    for(let a of atEndFuncs){
        p = a(p); // for things like doubling
    }

    return egScore;
}

export function checkDomination(p){
    return;
    if(scorePlayer(p) == Infinity) endGame('Domination')
}

export function allBeamed(p){
    let b = [];
    let playerIndex = getPlayerIndex(p);
    
    for(let d of Object.keys(p.decks)){
        for(let c of p.decks[d]){
            if(c.beamed){
                for(let cd of c.beamed){
                    if(cd.sourcePlayer == playerIndex) b.push(cd);
                }
            }
        }
    }

    for(let c of getNeutralZone()){
        if(c && c.beamed){
            for(let cd of c.beamed){
                if(cd.sourcePlayer == playerIndex) b.push(cd);
            }
        }
    }
    //console.log(b)
    return b;
}

export function allScoreableCards(p, finalScoring = false){
    return perfMeasure('score.allScoreableCards', () => {
        let cardsToScore = [];
        let presetOrder = ['duty', 'play', 'locations', 'fleet'];
        for(let o of presetOrder){
            if(!p.decks.hasOwnProperty(o)) continue;
            cardsToScore.push(...p.decks[o]);
        }
        for(let d of Object.keys(p.decks)){
            if(!p.decks.hasOwnProperty(d)) continue;
            if(presetOrder.includes(d)) continue; // so preset stuff has happened already
            // removed Continuum because of need to have 'when' reactions trigger
            if(d == 'dev' || d == 'reserve' || d == 'reinforcements' || d == 'missions' || d == 'journey' ) continue; // Missions scored separately
            cardsToScore.push(...p.decks[d]);
            /*for(let c of p.decks[d]){
                cardsToScore = [...cardsToScore, c];
            }*/
        }
        
        cardsToScore.push(...allBeamed(p));
        if(p.dashboard && p.dashboard.missions) cardsToScore.push(...p.dashboard.missions); // To avoid circular status issues NB Dec added missions back
        let ret = Array.from(new Set(cardsToScore));
        if(finalScoring){
            // Do any necessary tweaks
           if(p.dashboard && p.dashboard.passives) ret.push(...p.dashboard.passives); // 
        }
        return ret;
    }, {captain: p && p.captain, finalScoring});
}
